import {Component, computed, effect, inject, viewChild} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import {DatePipe} from '@angular/common';
import {PageGridComponent} from '../../../../shared/components/page-grid/page-grid.component';
import {GridEventOutput, GridModel} from '../../../../shared/model/page-grid';
import {ErpOutboundPanelFilterService} from '../../service/erp-outbound-panel-filter.service';
import {ErpOutboundReportGridService} from '../../service/erp-outbound-report-grid.service';
import {IErpOutboundItem} from '../../interfaces/erp-outbound-panel.interface';
import {erpOutboundResultChipStyle, isErpOutboundError} from '../../constants/erp-outbound-panel.constants';
import {ErpOutboundRawMessageDialogComponent} from '../erp-outbound-raw-message-dialog/erp-outbound-raw-message-dialog.component';

@Component({
  selector: 'app-erp-outbound-report-list',
  templateUrl: './erp-outbound-report-list.component.html',
  standalone: false,
  styles: [`
    /* Tint the error-indicator warning triangle red without affecting other icon columns. */
    ::ng-deep [data-testid$='-errorIndicator-cell'] .mat-icon,
    ::ng-deep [data-testid$='-errorIndicator-cell'] .mat-mdc-icon-button {
      color: #c62828 !important;
    }
  `],
})
export class ErpOutboundReportListComponent {
  private readonly filterService = inject(ErpOutboundPanelFilterService);
  private readonly gridService = inject(ErpOutboundReportGridService);
  private readonly datePipe = inject(DatePipe);
  private readonly dialog = inject(MatDialog);

  protected readonly gridRef = viewChild.required(PageGridComponent);

  protected readonly gridConfig = computed<GridModel<IErpOutboundItem>>(() => ({
    gridDataTestId: 'erp-outbound-report-list',
    gridTitle: 'ERP Outbound Report',
    defaultPageSize: 25,
    defaultSort: {active: 'dateCreated', direction: 'desc'},
    replayButton: true,
    isTotalsRowHidden: true,
    columns: [
      {
        columnProperty: 'legalEntityName',
        columnHeader: 'Legal Entity',
        sort: true,
        textLeft: true,
      },
      {
        columnProperty: 'pnr',
        columnHeader: 'PNR',
        sort: true,
        textLeft: true,
      },
      {
        columnProperty: 'erpOrderNumber',
        columnHeader: 'ERP Sales Order No.',
        sort: true,
        textLeft: true,
      },
      {
        columnProperty: 'queueName',
        columnHeader: 'Queue',
        sort: true,
        textLeft: true,
      },
      {
        columnProperty: 'responseType',
        columnHeader: 'Response Type',
        sort: true,
        textLeft: true,
        extraWidth: true,
      },
      {
        columnProperty: 'erpMessageId',
        columnHeader: 'ERP Message ID',
        sort: true,
        textLeft: true,
      },
      {
        columnProperty: 'adxInvoiceId',
        columnHeader: 'ADX Invoice ID',
        sort: true,
        clickableWithItem: (item: IErpOutboundItem) => !!item.adxInvoiceId,
        disablePreventEventLink: true,
        linkStyle: () => ({color: '#0078d4', 'text-decoration': 'underline', cursor: 'pointer'}),
        linkEventFunction: (item: IErpOutboundItem) => `AdxInvoices/${item.adxInvoiceId}`,
      },
      {
        columnProperty: 'invoiceNumber',
        columnHeader: 'ERP Invoice No.',
        sort: true,
        textLeft: true,
      },
      {
        shouldHideColumn: true,
        columnProperty: 'erpTrailerId',
        columnHeader: 'ERP Trailer ID',
        sort: true,
        textLeft: true,
      },
      {
        shouldHideColumn: true,
        columnProperty: 'erpAccountCode',
        columnHeader: 'ERP Account Code',
        sort: true,
        textLeft: true,
      },
      
      {
        shouldHideColumn: true,
        columnProperty: 'fileName',
        columnHeader: 'File Name',
        sort: true,
        textLeft: true,
        extraWidth: true,
      },
      {
        columnProperty: 'result',
        columnHeader: 'Result',
        sort: true,
        style: (item: IErpOutboundItem) => erpOutboundResultChipStyle(item.result)
      },
      {
        columnProperty: 'errorIndicator',
        columnHeader: '',
        sort: false,
        columnIconDisplayFunction: (item: IErpOutboundItem) =>
          isErpOutboundError(item.result, item.errorDescription) ? 'warning' : '',
      },
      {
        // Data retained for the error dialog, but hidden as a column — surfaced via the
        // warning indicator next to Result instead.
        shouldHideColumn: true,
        columnProperty: 'errorDescription',
        columnHeader: 'Error Description',
        sort: true,
        textLeft: true,
        wrapContent: true,
        extraWidth: true,
      },
      {
        columnProperty: 'rawMessage',
        columnHeader: 'Raw Message',
        sort: false,
        processData: (item) => item ? 'View' : '',
        linkStyle: () => ({color: '#0078d4', 'text-decoration': 'underline', cursor: 'pointer'}),
        clickableWithItem: (item) => !!item.rawMessage,
      },
      {
        columnProperty: 'dateCreated',
        columnHeader: 'Created',
        sort: true,
        processData: (item) => item ? (this.datePipe.transform(item, 'M/dd/yyyy HH:mm') ?? '') : '',
      },
      {
        shouldHideColumn: true,
        columnProperty: 'dateUpdated',
        columnHeader: 'Updated',
        sort: true,
        processData: (item) => item ? (this.datePipe.transform(item, 'M/dd/yyyy HH:mm') ?? '') : '',
      },
    ],
    gridGetService: this.gridService,
  }));

  constructor() {
    effect(() => {
      const filters = this.filterService.filtersValue();
      if (!filters.ReloadGrid) return;
      this.gridRef().getItems();
    });
  }

  protected handleColumnClick(event: GridEventOutput<IErpOutboundItem>): void {
    if (event.column === 'rawMessage' && event.item.rawMessage) {
      this.dialog.open(ErpOutboundRawMessageDialogComponent, {
        width: '800px',
        maxWidth: '95vw',
        data: {title: 'Raw Message', content: event.item.rawMessage},
      });
      return;
    }

    if (event.column === 'errorIndicator') {
      this.dialog.open(ErpOutboundRawMessageDialogComponent, {
        width: '800px',
        maxWidth: '95vw',
        data: {
          title: 'Error Description',
          content: event.item.errorDescription || 'No error description provided.',
        },
      });
    }
  }
}
